import { TimeEventType } from "@/models/time-control/timeStateEvents";
import { useCurrTimeState, useTimeService } from "@/models/time-control/timeStateHooks";
import { useAppDispatch } from "@/reducer";
import { adjustAllCountryPlotDateTimeRange } from "@/reducer/CountryPlotState";
import { adjustAllEnergyPlotDateTimeRange } from "@/reducer/EnergyPlotsState";
import { adjustAllLocationTableDateTimeRange } from "@/reducer/LocationTablesState";
import { adjustAllPlotDateTimeRange } from "@/reducer/PlotsState";
import { adjustAllTableDateTimeRange } from "@/reducer/WeatherTablesState";
import debounce from "lodash/debounce";
import type { DateTime, Duration } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

type TimeHandler = {
  time: DateTime;
  setTime: (newTime: DateTime) => void;
  registerSetTime: (callback: (time: DateTime) => void) => void;
};

let timeHandlerInstance: TimeHandler | null = null;

const createTimeHandler = (initialTime: DateTime): TimeHandler => {
  let currentTime = initialTime;
  let setTimeCallback: ((time: DateTime) => void) | null = null;

  return {
    get time() {
      return currentTime;
    },
    setTime: (newTime: DateTime) => {
      currentTime = newTime;
      if (setTimeCallback) {
        setTimeCallback(newTime);
      }
    },
    registerSetTime: (callback: (time: DateTime) => void) => {
      setTimeCallback = callback;
    },
  };
};

type useTimeHandlerProps = {
  debounceMs?: number;
};

export const useTimeHandler = ({ debounceMs = 200 }: useTimeHandlerProps = {}) => {
  const timeState = useCurrTimeState();
  const { send } = useTimeService();
  const dispatch = useAppDispatch();

  const shownDisplayTime = timeState.context.nextDisplayTime_;

  const handler = useRef<TimeHandler>();
  if (!handler.current) {
    if (!timeHandlerInstance) {
      timeHandlerInstance = createTimeHandler(shownDisplayTime);
    }
    handler.current = timeHandlerInstance;
  }

  const [time, setTime] = useState(handler.current.time);

  useEffect(() => {
    setTime(shownDisplayTime);
    if (handler.current) {
      handler.current.setTime(shownDisplayTime);
    }
  }, [shownDisplayTime]);

  useRef(() => {
    if (handler.current) {
      handler.current.registerSetTime(setTime);
    }
  }).current();

  const debouncedSend = useCallback(
    debounce((newTime: DateTime) => {
      send({
        type: TimeEventType.updateNextDisplayTime,
        nextDisplayTime_: newTime,
      });
    }, debounceMs),
    [send, debounceMs],
  );

  const handleTimeChange = useCallback(
    (duration: Duration) => {
      if (!handler.current) {
        return;
      }

      const updatedTime = handler.current.time.plus(duration);
      const durationMinutes = duration.as("minutes");
      handler.current.setTime(updatedTime);

      dispatch(adjustAllPlotDateTimeRange({ durationMinutes }));
      dispatch(adjustAllCountryPlotDateTimeRange({ durationMinutes }));
      dispatch(adjustAllLocationTableDateTimeRange({ durationMinutes }));
      dispatch(adjustAllTableDateTimeRange({ durationMinutes }));
      dispatch(adjustAllEnergyPlotDateTimeRange({ durationMinutes }));
      debouncedSend(updatedTime);
    },
    [dispatch, debouncedSend],
  );

  return { time, handleTimeChange };
};
